import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { register, validarInvitacion } from '../services/api';
import './Login.css';

function Register({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || '';

  const [formData, setFormData] = useState({
    email: '', password: '', nombre: '', apellidos: '', piso: '', telefono: '', rol: 'vecino'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Estado de la invitación
  const [invitacion, setInvitacion] = useState(null);       // { comunidad_id, comunidad_nombre, email_restringido }
  const [invError, setInvError] = useState('');
  const [invLoading, setInvLoading] = useState(false);

  // Validar el token al cargar la página si existe
  useEffect(() => {
    if (!inviteToken) return;
    setInvLoading(true);
    validarInvitacion(inviteToken)
      .then(res => {
        setInvitacion(res.data);
        if (res.data.email_restringido) {
          setFormData(fd => ({ ...fd, email: res.data.email_restringido }));
        }
      })
      .catch(err => {
        setInvError(err.response?.data?.error || 'Link de invitación no válido o caducado');
      })
      .finally(() => setInvLoading(false));
  }, [inviteToken]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inviteToken && !invitacion) {
      setError('El link de invitación no es válido. No puedes registrarte sin uno válido.');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData };
      if (inviteToken) payload.invite_token = inviteToken;
      const response = await register(payload);
      // El token va en cookie httpOnly — solo guardamos datos de usuario para la UI
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  // Si hay token pero no es válido, mostramos error bloqueante
  if (inviteToken && invLoading) {
    return (
      <div className="login-root">
        <div className="login-panel-right">
          <div className="login-form-wrap">
            <p style={{ textAlign: 'center', color: '#aaa' }}>Validando invitación...</p>
          </div>
        </div>
      </div>
    );
  }

  if (inviteToken && invError) {
    return (
      <div className="login-root">
        <div className="login-panel-right">
          <div className="login-form-wrap">
            <div className="login-form-header">
              <h1 className="login-form-title">Invitación no válida</h1>
            </div>
            <div className="alert alert-error">{invError}</div>
            <p style={{ textAlign: 'center', marginTop: '1rem', color: '#8B85A7' }}>
              Solicita un nuevo link a tu administrador o presidente de comunidad.
            </p>
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Link to="/login">Volver al inicio de sesión</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-root">
      <div className="login-panel-right">
        <div className="login-form-wrap" style={{ maxWidth: '520px' }}>
          <div className="login-form-header">
            <h1 className="login-form-title">Crear Cuenta</h1>
            {invitacion ? (
              <p className="login-form-sub">Te has unido a <strong>{invitacion.comunidad_nombre}</strong></p>
            ) : (
              <p className="login-form-sub">Regístrate en tu comunidad</p>
            )}
          </div>

        {/* Banner de comunidad asignada */}
        {invitacion && (
          <div style={{
            background: 'linear-gradient(135deg, #667eea22, #764ba222)',
            border: '1px solid #667eea55',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '1.4rem' }}>🏢</span>
            <div>
              <div style={{ fontWeight: '600', color: '#667eea' }}>{invitacion.comunidad_nombre}</div>
              <div style={{ fontSize: '0.82rem', color: '#aaa' }}>Comunidad asignada automáticamente</div>
            </div>
          </div>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Nombre *</label>
              <input type="text" name="nombre" className="form-control" value={formData.nombre} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Apellidos</label>
              <input type="text" name="apellidos" className="form-control" value={formData.apellidos} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              required
              readOnly={!!invitacion?.email_restringido}
              style={invitacion?.email_restringido ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Contraseña *</label>
            <input type="password" name="password" className="form-control" value={formData.password} onChange={handleChange} required />
          </div>
          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Piso</label>
              <input type="text" name="piso" className="form-control" value={formData.piso} onChange={handleChange} placeholder="1º A" />
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input type="tel" name="telefono" className="form-control" value={formData.telefono} onChange={handleChange} />
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Registrando...' : 'Registrarse'}
          </button>
        </form>
          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#8B85A7' }}>
            ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
